import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ProfileDataService } from '../profile-data.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ProfileService } from '../profile.service';
import { Profile } from '../../shared/configurations/profile.constant';
import { SEOService } from '../../core/seo.service';
import { Identifiers } from '../../shared/configurations/identifiers.constant';
import { JwtUtilService } from '../../core/jwt-util.service';
import { Subscription } from 'rxjs';
import { I18nService } from '../../core/i18n.service';
import { DefaultTranslatePipe } from '../../shared/pipes/default-translate.pipe';
import { EspDateFormatPipe } from '../../shared/pipes/date-format.pipe';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { OrcidLinkComponent } from '@exl-ng/mulo-common';
import { MatTooltip } from '@angular/material/tooltip';
import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { ProfileHighlightsAwardsComponent } from '../profile-highlights-awards/profile-highlights-awards.component';
import { ProfileHighlightsComponent } from '../profile-highlights/profile-highlights.component';
import { ProfileOverviewAffiliationDisplayComponent } from './profile-overview-affiliation-display/profile-overview-affiliation-display.component';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon } from '@angular/material/list';
import { HtmlDisplayComponent } from '../../parts/html-display/html-display.component';
import { ExpandableTextComponent } from '../../parts/expandable-text/expandable-text.component';
import { ContentBoxComponent } from '../../parts/content-box/content-box.component';
import { NgClass, KeyValuePipe } from '@angular/common';

@Component({
    selector: 'esp-profile-overview',
    templateUrl: './profile-overview.component.html',
    styleUrls: ['./profile-overview.component.scss'],
    imports: [
        ContentBoxComponent,
        ExpandableTextComponent,
        HtmlDisplayComponent,
        MatList,
        MatListItem,
        MatIcon,
        MatListItemIcon,
        NgClass,
        ProfileOverviewAffiliationDisplayComponent,
        ProfileHighlightsComponent,
        ProfileHighlightsAwardsComponent,
        SvgViewboxDirective,
        MatTooltip,
        OrcidLinkComponent,
        ContentLoaderModule,
        TranslateModule,
        KeyValuePipe,
        EspDateFormatPipe,
        DefaultTranslatePipe,
    ],
})
export class ProfileOverviewComponent implements OnInit, OnDestroy {
    profileDataService = inject(ProfileDataService);
    private SEOService = inject(SEOService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private translate = inject(TranslateService);
    profileService = inject(ProfileService);
    private jwtUtilService = inject(JwtUtilService);
    i18nSvc = inject(I18nService);

    nameSubscription: Subscription;
    mainInstName;
    readonly metricIconMap = {
        outputViews: 'eye-outline',
        fileViewsDownloads: 'download',
        totalCitations: 'message-text',
    };

    ngOnInit() {
        this.mainInstName =
            this.configurationHandlerService.mainInstitutionName();
        //deep link to the direct tab - need to wait until the name updates
        this.nameSubscription = this.profileService.nameChange$.subscribe(
            (res) => {
                this.changeDocumentTitle(res);
            },
        );
        //moving between tabs
        if (this.profileDataService.displayName.value) {
            this.changeDocumentTitle(this.profileDataService.displayName.value);
        }
    }

    changeDocumentTitle(userName) {
        let code = '';
        if (this.profileService.isPublicPage) {
            code = 'research.public.profile.overview.tab.document.title';
        } else {
            code = 'research.profile.overview.tab.document.title';
        }
        this.translate
            .get(code, { value0: userName, value1: this.mainInstName })
            .subscribe((res) => {
                this.SEOService.updateTitle(res);
            });
    }

    getLinksSectionTitle() {
        if (this.profileDataService.links.value.length === 1) {
            return 'research.profile.overview.link';
        }
        return 'research.profile.overview.links';
    }

    getAddressesSectionTitle() {
        if (this.profileDataService.addresses.value.length === 1) {
            return 'research.profile.overview.address';
        }
        return 'research.profile.overview.addresses';
    }

    getEmailsSectionTitle() {
        if (this.profileDataService.emails.value.length === 1) {
            return 'research.profile.overview.email';
        }
        return 'research.profile.overview.emails';
    }

    getPhonesSectionTitle() {
        if (this.profileDataService.phones.value.length === 1) {
            return 'research.profile.overview.phone';
        }
        return 'research.profile.overview.phones';
    }

    displayFirstSection() {
        if (
            (this.profileDataService.expertise.value &&
                this.profileDataService.expertise.value.length > 0) ||
            (this.profileDataService.links.value &&
                this.profileDataService.links.value.length > 0) ||
            (this.profileDataService.curAffiliations.value &&
                this.profileDataService.curAffiliations.value.length > 0) ||
            (this.profileDataService.prevAffiliations.value &&
                this.profileDataService.prevAffiliations.value.length > 0) ||
            (this.profileDataService.researcherHighlights &&
                this.profileDataService.researcherHighlights.length > 0) ||
            (this.profileDataService.educations &&
                this.profileDataService.educations.length > 0) ||
            this.profileDataService.researcherGrantsHighlights?.length ||
            this.profileDataService.honors.value?.length ||
            this.profileDataService.engagements.value?.length
        ) {
            return true;
        }
        return false;
    }

    displayAsHref(identifier) {
        if (
            identifier.type === Identifiers.ORCID ||
            identifier.type === Identifiers.SCOPUS ||
            identifier.type === Identifiers.INSI ||
            identifier.type === Identifiers.RESEARCHER_ID ||
            identifier.type === Identifiers.VIAF ||
            identifier.type === Identifiers.WIKIDATA ||
            identifier.type === Identifiers.GOOGLE_SCHOLAR_ID ||
            identifier.type === Identifiers.ARXIV ||
            (identifier.type === Identifiers.PIVOT && this.isPrivateMode())
        ) {
            return true;
        }
        return false;
    }

    isOrcid(identifier) {
        if (identifier.type === Identifiers.ORCID) {
            return true;
        }
        return false;
    }

    isWosId = (identifier) => identifier.type === Identifiers.RESEARCHER_ID;

    isPrivateMode() {
        return (
            !this.profileService.isPublicPage &&
            !this.jwtUtilService.getCurrentUserForProxy()
        );
    }

    getHref(identifier) {
        const type = identifier.type;
        const value = identifier.value;
        if (type === Identifiers.ORCID) {
            return Profile.ORCID_URL_PREFIX + value;
        } else if (type === Identifiers.SCOPUS) {
            return Profile.SCOPUS_ID_URL_PREFIX + value;
        } else if (type === Identifiers.INSI) {
            return Profile.ISNI_URL_PREFIX + value;
        } else if (type === Identifiers.RESEARCHER_ID) {
            return Profile.RESEARCHER_ID_URL_PREFIX + value;
        } else if (type === Identifiers.VIAF) {
            return Profile.VIAF_URL_PREFIX + value;
        } else if (type === Identifiers.WIKIDATA) {
            return Profile.WIKIDATA_ID_URL_PREFIX + value;
        } else if (type === Identifiers.GOOGLE_SCHOLAR_ID) {
            return Profile.GOOGLE_SCHOLAR_ID_URL_PREFIX + value;
        } else if (type === Identifiers.ARXIV) {
            return Profile.ARXIV_ID_URL_PREFIX + value;
        } else if (type === Identifiers.PIVOT) {
            return Profile.PIVOT_ID_URL_PREFIX + value;
        }
        return '';
    }

    ngOnDestroy() {
        if (this.nameSubscription) {
            this.nameSubscription.unsubscribe();
        }
    }

    get haveAsideDate() {
        if (
            (this.profileDataService.identifiers.value &&
                this.profileDataService.identifiers.value.length > 0) ||
            (this.profileDataService.emails.value &&
                this.profileDataService.emails.value.length > 0) ||
            (this.profileDataService.phones.value &&
                this.profileDataService.phones.value.length > 0) ||
            (this.profileDataService.addresses.value &&
                this.profileDataService.addresses.value.length > 0)
        ) {
            return true;
        }
        return false;
    }
}
